import React from 'react';
import ReactGA from 'react-ga';
import landingVaDisabilityClaim from '../../../assets/images/landingVaDisabilityClaim.png';
import dividerBrush from '../../../assets/images/conditionsCaseStudy/divider-brush.png';
import whyInfographic from '../../../assets/images/conditionsCaseStudy/why-infographic.png';
import contextNotebook from '../../../assets/images/conditionsCaseStudy/context-notebook.svg';
import takeawaysEmoji from '../../../assets/images/conditionsCaseStudy/takeaways-emoji.svg';
import abPrototypes from '../../../assets/images/conditionsCaseStudy/ab-prototypes.png';
import logoZoomText from '../../../assets/images/conditionsCaseStudy/logo-zoomtext.png';
import logoVoiceOver from '../../../assets/images/conditionsCaseStudy/logo-voiceover.png';
import logoJaws from '../../../assets/images/conditionsCaseStudy/logo-jaws.png';
import researchDemographics from '../../../assets/images/conditionsCaseStudy/research-demographics.png';
import outcomeCauseBefore from '../../../assets/images/conditionsCaseStudy/outcome-cause-before.svg';
import outcomeCauseAfter from '../../../assets/images/conditionsCaseStudy/outcome-cause-after.png';
import {
  CaseStudyBulletList,
  CaseStudyContentCard,
  CaseStudyDivider,
  CaseStudyDoubleCardRow,
  CaseStudyHero,
  CaseStudyOverviewColumn,
  CaseStudyOverviewGrid,
  CaseStudySecondaryNav,
  CaseStudySectionHeader,
  CaseStudySplitSection,
  CaseStudyStatsBar,
} from '../../../components/cards/caseStudyCards/caseStudyCards';
import ConditionsDesignSection, { Finding1BeforeAfterArrow } from './conditionsDesignSection';
import ds from './conditionsDesignSection.module.scss';
import ScrollToTopButton from '../../../components/scrollToTopButton/ScrollToTopButton';
import cards from '../../../components/cards/caseStudyCards/caseStudyCards.module.scss';
import page from './conditionsCaseStudy.module.scss';

const VA_FORM_URL =
  'https://www.va.gov/disability/file-disability-claim-form-21-526ez/introduction';
const VBA_REPORTS_URL = 'https://www.benefits.va.gov/reports/detailed_claims_data.asp#:~:text=Veterans%20Affairs%20(VA)%20continues%20to,ability%20to%20meet%20these%20goals.';

const NAV_ITEMS = [
  { id: 'section-intro', label: 'Intro' },
  { id: 'section-the-why', label: 'The Why' },
  { id: 'section-context', label: 'Context' },
  { id: 'section-problem', label: 'Problem Definition' },
  { id: 'section-research', label: 'Research' },
  { id: 'section-design', label: 'Design Based On Findings' },
  { id: 'section-outcome', label: 'Outcome' },
  { id: 'section-my-role', label: 'My Role' },
  { id: 'section-takeaways', label: 'Takeaways' },
];

const HERO_TAGS = [
  'User Research',
  'UX Design',
  'Civic Tech',
  'Enterprise',
  'A/B Testing',
  'Accessibility',
  'Design System',
  'Mobile-first web design',
];

export default function ConditionsCaseStudy() {
  const [activeSection, setActiveSection] = React.useState('section-intro');
  const scrollLockRef = React.useRef(null);
  const scrollLockTimerRef = React.useRef(null);
  const navWrapRef = React.useRef(null);

  ReactGA.pageview(window.location.pathname + window.location.search);

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const updateActiveFromScroll = React.useCallback(() => {
    if (scrollLockRef.current) return;

    const scrollSpyOffset = (navWrapRef.current?.offsetHeight ?? 72) + 48;
    const scrollPos = window.scrollY + scrollSpyOffset;
    let current = NAV_ITEMS[0].id;

    for (const { id } of NAV_ITEMS) {
      const el = document.getElementById(id);
      if (!el) continue;
      const top = el.getBoundingClientRect().top + window.scrollY;
      if (top <= scrollPos) {
        current = id;
      }
    }

    setActiveSection(current);
  }, []);

  React.useEffect(() => {
    updateActiveFromScroll();

    const onScroll = () => {
      updateActiveFromScroll();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateActiveFromScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateActiveFromScroll);
      if (scrollLockTimerRef.current) {
        window.clearTimeout(scrollLockTimerRef.current);
      }
    };
  }, [updateActiveFromScroll]);

  const scrollToSection = (id) => {
    if (scrollLockTimerRef.current) {
      window.clearTimeout(scrollLockTimerRef.current);
    }

    scrollLockRef.current = id;
    setActiveSection(id);

    const el = document.getElementById(id);
    if (el) {
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      const navH = navWrapRef.current?.offsetHeight ?? 72;
      // Intro: nav is below hero — don’t subtract nav height or we’d leave a gap above the bar for later sections.
      const targetTop = id === 'section-intro' ? docTop : docTop - navH;
      window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    }

    scrollLockTimerRef.current = window.setTimeout(() => {
      scrollLockRef.current = null;
      scrollLockTimerRef.current = null;
      updateActiveFromScroll();
    }, 900);
  };

  return (
    <main className={page.page}>
      <div id="section-intro">
        <div className={`${page.inner} ${page.heroBand}`}>
          <CaseStudyHero
            title="Designing for Dignity: Improving Accessibility & Data Integrity in a High-Stakes Disability Claim Flow"
            tags={HERO_TAGS}
            subhead="Usability research informed design improvements to VA form 21-526EZ"
            ctaHref={VA_FORM_URL}
            ctaLabel="View VA.gov 21-526EZ Form"
            heroImageSrc={landingVaDisabilityClaim}
            heroImageAlt="Laptop showing the VA.gov disability compensation claim flow"
          />
        </div>

        <div className={`${page.inner} ${page.overviewBand}`}>
          <CaseStudyOverviewGrid>
            <CaseStudyOverviewColumn label="Timeline">
              <ul className={cards.overviewList}>
                <li>6 weeks</li>
              </ul>
            </CaseStudyOverviewColumn>
            <CaseStudyOverviewColumn label="My role">
              <ul className={cards.overviewList}>
                <li>UX Researcher</li>
                <li>UX Designer</li>
              </ul>
            </CaseStudyOverviewColumn>
            <CaseStudyOverviewColumn label="Team">
              <ul className={cards.overviewList}>
                <li>2 UX Designer/Researchers</li>
                <li>Product Manager</li>
                <li>Accessibility Engineer</li>
                <li>2 Engineers</li>
                <li>Design System partners</li>
              </ul>
            </CaseStudyOverviewColumn>
            <CaseStudyOverviewColumn label="Scope" wide>
              <ul className={cards.overviewList}>
                <li>
                  Evaluated a shift from a legacy single-page pattern to a multi-step condition-by-condition
                  flow
                </li>
                <li>Identified systemic accessibility breakdowns</li>
                <li>Improved structured data capture (laterality, dates, cause attribution)</li>
              </ul>
            </CaseStudyOverviewColumn>
          </CaseStudyOverviewGrid>
        </div>
      </div>

      <div ref={navWrapRef} className={page.navWrap}>
        <CaseStudySecondaryNav
          items={NAV_ITEMS}
          activeId={activeSection}
          onSelect={scrollToSection}
        />
      </div>

      <section className={`${page.section} ${page.sectionDarkPorcelain}`} id="section-the-why">
        <div className={page.inner}>
          <CaseStudySplitSection
            kicker="THE WHY"
            title="The Moment That Framed the Work"
            imageSrc={whyInfographic}
            imageAlt="Graphic highlighting volume of VA form 526-EZ submissions"
            captionHref={VBA_REPORTS_URL}
            captionLabel="Veteran Benefits Administration Reports"
          >
            <p className={cards.splitBody}>
              During usability testing, a screen reader user got stuck in a date dropdown that announced
              itself as &ldquo;MM.&rdquo; They didn&apos;t know it was a dropdown. They didn&apos;t know how
              to navigate it. And this was just one field in one of the most complex forms at the VA that
              determines a Veteran&apos;s access to their disability benefits. That moment clarified the real
              problem:
            </p>
            <p className={cards.splitHighlight}>
              This wasn&apos;t about optimizing a form. It was about removing invisible barriers that could
              delay or jeopardize a Veteran&apos;s benefits.
            </p>
          </CaseStudySplitSection>
        </div>
      </section>

      <section className={`${page.section} ${page.sectionPorcelain}`} id="section-context">
        <div className={page.inner}>
          <CaseStudySplitSection
            kicker="CONTEXT"
            title="Potential $8 billion+ Impact"
            imageSrc={contextNotebook}
            imageAlt="Notebook-style illustration estimating annual impact of disability claims"
            captionHref={VBA_REPORTS_URL}
            captionLabel="Veteran Benefits Administration Reports"
            footerGap80
            splitFooter={
              <p className={`${cards.splitHighlight} ${cards.splitHighlightLg}`}>
                &hellip;make the online process more accessible for claimants who use assistive technologies.
              </p>
            }
          >
            <div className={cards.splitBody}>
              <p>
                The &ldquo;conditions&rdquo; entry step of the VA disability claim (form 21-526EZ) is a
                critical page of a very complex form. This section of the form allows claimants to provide
                required information on medical conditions they wish to have reviewed by the VA to
                determine if they are eligible for disability benefits.
              </p>
              <p>
                In 2025, over 2 million disability benefits claims were processed online, and Veterans can
                be awarded over $4,000 a month in disability benefits; so the impact is great and the need
                to have a streamlined and accessible design is clear.
              </p>
              <p>
                For this effort, I led research and design for the redesign of the &ldquo;Conditions&rdquo;
                entry step of the VA disability claim (Form 21-526EZ), focusing specifically on Veterans
                using assistive technology. Our team had recently completed an evaluation of new design
                standards with a non-AT test group and made key improvements to the flow based on findings
                from the research. With a more refined design, it was critical that we test these design
                iterations and validate them with assistive technology users.
              </p>
            </div>
          </CaseStudySplitSection>
        </div>
      </section>

      <section className={`${page.section} ${page.sectionDarkPorcelain}`} id="section-problem">
        <div className={page.inner}>
          <CaseStudySplitSection
            kicker="PROBLEM DEFINITION"
            title="The Real Problem Beneath the UI"
            imageSrc={abPrototypes}
            imageAlt="Figma A/B prototypes comparing Cherry condition-first flow and Apple condition-by-condition flow"
            captionLabel="Screenshot of Figma A/B Prototypes"
            captionCenter
            largeFooterGap
            figureClassName={cards.splitFigureTall}
            splitFooter={
              <p className={cards.splitFooterQuestion}>
                How may we design a flow that improves accessibility, increases data integrity, and
                strengthens trust &mdash; without overwhelming users?
              </p>
            }
          >
            <div className={cards.splitBodyColumn}>
              <p>At a surface level, the team was deciding between two patterns:</p>
              <ul>
                <li>Cherry &ndash; List all conditions first, then answer details</li>
                <li>Apple &ndash; Complete one condition fully before moving to the next</li>
              </ul>
              <p>But research revealed something deeper:</p>
              <ul>
                <li>Laterality (left/right) was inconsistently captured, causing adjudication delays.</li>
                <li>Assistive tech users struggled to discover autosuggestions.</li>
                <li>Date questions were ambiguous (diagnosis? onset? service date?).</li>
                <li>Design system components were announcing incorrect instructions to screen readers.</li>
                <li>Focus behavior broke context across steps.</li>
              </ul>
              <p className={cards.splitBodyEmphasis}>
                The problem wasn&apos;t &ldquo;Which pattern feels better?&rdquo; It was:
              </p>
            </div>
          </CaseStudySplitSection>
        </div>
      </section>

      <section className={`${page.section} ${page.sectionPorcelain}`} id="section-research">
        <div className={page.inner}>
          <CaseStudySplitSection
            kicker="RESEARCH"
            title="Learning Directly From Veterans Who Use Assistive Technologies"
            splitMedia={
              <div className={cards.researchMediaColumn}>
                <div className={cards.researchMediaBlock}>
                  <div className={cards.researchLogoRow}>
                    <div className={cards.researchLogoCell}>
                      <img
                        className={`${cards.researchLogo} ${cards.researchLogoBoost}`}
                        src={logoZoomText}
                        alt="ZoomText"
                        loading="lazy"
                      />
                    </div>
                    <div className={cards.researchLogoCell}>
                      <img
                        className={cards.researchLogo}
                        src={logoVoiceOver}
                        alt="VoiceOver"
                        loading="lazy"
                      />
                    </div>
                    <div className={cards.researchLogoCell}>
                      <img
                        className={`${cards.researchLogo} ${cards.researchLogoBoost}`}
                        src={logoJaws}
                        alt="JAWS"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className={cards.mediaCaption}>
                    <p className={cards.mediaCaptionPlain}>
                      Logos of assistive technologies used: JAWS, ZoomText and Voice Over
                    </p>
                  </div>
                </div>
                <div className={cards.researchMediaBlock}>
                  <figure className={cards.researchDemoFigure}>
                    <img
                      className={cards.researchDemoImg}
                      src={researchDemographics}
                      alt="Spreadsheet titled 2024-11 Condition Information Entry AT Testing with participant demographics and assistive technology categories"
                      loading="lazy"
                    />
                  </figure>
                  <div className={`${cards.mediaCaption} ${cards.mediaCaptionCenter}`}>
                    <p className={`${cards.mediaCaptionPlain} ${cards.mediaCaptionPlainCenter}`}>
                      Screenshot of user testing demographics
                    </p>
                  </div>
                </div>
              </div>
            }
          >
            <div className={cards.researchLeftStack}>
              <div className={cards.researchSubBlock}>
                <p className={cards.researchSubLabel}>STUDY STRUCTURE</p>
                <p className={cards.researchSubText}>1-1.5 hour long remote usability A/B testing</p>
              </div>
              <div className={cards.researchSubBlock}>
                <p className={cards.researchSubLabel}>PARTICIPANTS</p>
                <p className={cards.researchSubText}>
                  7 Veterans who all use assistive technologies. We wanted diversity.
                </p>
                <ul className={cards.researchList}>
                  <li>
                    4 screen reader users
                    <ul>
                      <li>Desktop screen reader: JAWS, Fusion</li>
                      <li>Mobile screen reader: Voice Over</li>
                    </ul>
                  </li>
                  <li>
                    3 magnifier users
                    <ul>
                      <li>ZoomText</li>
                    </ul>
                  </li>
                </ul>
              </div>
              <div className={cards.researchSubBlock}>
                <p className={cards.researchSubLabel}>KEY IMPACT</p>
                <ul className={cards.researchList}>
                  <li>94.3% overall task success</li>
                  <li>100% success editing/removing conditions</li>
                  <li>Trust score increased to 4.3/5</li>
                </ul>
              </div>
            </div>
          </CaseStudySplitSection>
        </div>
      </section>

      <section className={`${page.section} ${page.sectionDarkPorcelain}`} id="section-design">
        <div className={page.inner}>
          <ConditionsDesignSection />
        </div>
      </section>

      <section className={`${page.section} ${page.sectionOutcomeTight}`} id="section-outcome">
        <div className={page.inner}>
          <div className={cards.sectionBlock}>
            <CaseStudySectionHeader kicker="OUTCOME" title="Outcomes That Changed Direction" />
            <div className={cards.outcomeRow}>
              <div className={cards.outcomeMain}>
                <p className={page.outcomeIntro}>Because design decisions were evidence-backed, we were able to:</p>
                <ul className={page.outcomeList}>
                  <li>Recommend Apple (multi-step) as the strategic direction</li>
                  <li>Justify design system investment</li>
                  <li>Prioritize accessibility bug fixes in sprint planning</li>
                  <li>Establish a clearer roadmap for future end-to-end testing</li>
                  <li>Identify key screens to focus on for design iterations</li>
                </ul>
                <p className={page.outcomeClosing}>And the measurable results supported the shift:</p>
              </div>
              <CaseStudyContentCard label="Next steps" surface="tan">
                <CaseStudyBulletList
                  items={[
                    'Test the multi-step pattern across the entire end-to-end form.',
                    'Expand recruitment to cognitive and speech-input users for further usability research.',
                    'Measure adjudication time improvements post-launch.',
                  ]}
                />
              </CaseStudyContentCard>
            </div>
          </div>
          <div className={page.statsBarOutcomeWrap}>
            <CaseStudyStatsBar
              animateOnScroll
              className={page.statsBarOutcomeSingleRow}
              donutVariant="cdOutcome"
              stats={[
                { value: '100%', label: 'edit/remove success', progress: 1 },
                { value: '94.3%', label: 'overall task success', progress: 0.943 },
                { value: '4.3/5', label: 'trust rating', progress: 0.86 },
                { value: '4.1/5', label: 'experience rating', progress: 0.82 },
              ]}
            />
          </div>
          <div className={page.outcomeCauseBeforeAfter}>
            <div className={`${ds.beforeAfter} ${ds.beforeAfterUnevenMocks}`}>
              <div className={ds.beforeAfterBottom}>
                <div className={`${ds.beforeAfterCol} ${ds.beforeAfterColBefore}`}>
                  <p className={ds.mockLabel}>{'   '}BEFORE</p>
                  <div className={`${ds.mockBefore} ${page.outcomeCauseMockBefore}`}>
                    <img
                      src={outcomeCauseBefore}
                      alt='Earlier “Cause of asthma” screen with basic radio options'
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className={ds.beforeAfterFlowArrow}>
                  <Finding1BeforeAfterArrow />
                </div>
                <div className={`${ds.beforeAfterCol} ${ds.beforeAfterColAfter}`}>
                  <p className={`${ds.mockLabel} ${ds.mockLabelLg}`}>AFTER</p>
                  <div className={ds.mockAfter}>
                    <img
                      src={outcomeCauseAfter}
                      alt='Updated “Cause of asthma” flow with clearer layout, progress, and actions'
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={page.sectionMyRole} id="section-my-role">
        <div className={page.inner}>
          <div className={cards.sectionBlock}>
            <CaseStudySectionHeader kicker="MY ROLE" title="Leadership and Systems Thinking" />
            <CaseStudyDoubleCardRow
              left={
                <CaseStudyContentCard label="Design decision" surface="porcelain">
                  <CaseStudyBulletList
                    items={[
                      'Reframed debate from UI preference to data integrity + equity',
                      'Escalated accessibility defects to design system governance',
                      'Made tradeoffs transparent to stakeholders',
                      'Surfaced research limitations (e.g., underrepresented groups) responsibly',
                    ]}
                  />
                </CaseStudyContentCard>
              }
              right={
                <CaseStudyContentCard label="Research evidence" surface="porcelain">
                  <p className={cards.cardIntro}>This project required navigating:</p>
                  <CaseStudyBulletList
                    items={[
                      'Accessibility compliance',
                      'Policy constraints',
                      'Engineering feasibility',
                      'Data classification logic',
                      'Adjudication needs',
                    ]}
                  />
                  <p className={cards.cardFooter}>
                    Screen reader users were disproportionately affected by technical issues.
                  </p>
                </CaseStudyContentCard>
              }
            />
            <p className={cards.splitFooterQuestion}>
              This wasn&apos;t incremental UX polish — it was structural risk reduction.
            </p>
          </div>
        </div>
      </section>

      <section className={page.section} id="section-takeaways">
        <div className={page.inner}>
          <div className={page.takeawaysBlock}>
            <CaseStudySectionHeader
              kicker="TAKEAWAYS"
              title="I Don&apos;t Just Improve Interfaces. I Strengthen Systems."
              titleClassName={cards.sectionTitleLarge}
              asideImg={takeawaysEmoji}
              asideAlt="Decorative avatar"
              asideImgClassName={cards.sectionAsideImgSm}
            />
            <CaseStudyDoubleCardRow
              left={
                <CaseStudyContentCard label="What I learned" surface="none" labelTone="teal">
                  <CaseStudyBulletList
                    items={[
                      'Learnability can outweigh speed in multi-step forms.',
                      'Accessibility research often exposes systemic technical debt.',
                      'Data structure decisions impact operational efficiency downstream.',
                      'Trust can be measured — and designed for.',
                    ]}
                  />
                </CaseStudyContentCard>
              }
              right={
                <CaseStudyContentCard label="What this work demonstrates" surface="none" labelTone="teal">
                  <CaseStudyBulletList
                    items={[
                      'I translate research findings into strategic decisions',
                      'I balance user dignity with operational realities',
                      'I advocate for systemic fixes over temporary patches',
                      'I use evidence to influence cross-functional teams',
                      'I design for equity in regulated, high-complexity environments',
                    ]}
                  />
                </CaseStudyContentCard>
              }
            />
          </div>
        </div>
      </section>
      <ScrollToTopButton />
    </main>
  );
}
